import { useEffect, useMemo, useState } from "react";
import {
  BookOpen,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Clock,
  Edit3,
  ExternalLink,
  FileCheck2,
  Plus,
  Save,
  Users,
  X,
} from "lucide-react";

import {
  SelfCourse,
  SelfCourseEnrollment,
  SelfCourseLessonType,
  SelfCourseQuizQuestion,
  selfCoursesApi,
} from "../api/selfCourses";
import MaterialDropzone from "../components/NewView/Common/MaterialDropzone";
import ProgramMaterials from "../components/NewView/Common/ProgramMaterials";

const lessonTypeOptions: Array<{ value: SelfCourseLessonType; label: string }> = [
  { value: "video", label: "Відео" },
  { value: "material", label: "Матеріал" },
  { value: "lab", label: "Лабораторна" },
  { value: "quiz", label: "Тест" },
  { value: "assignment", label: "Завдання" },
];

const AdminSelfCoursesPage = () => {
  const [selectedId, setSelectedId] = useState("");
  const [selectedCourse, setSelectedCourse] = useState<SelfCourse | null>(null);
  const [enrollments, setEnrollments] = useState<SelfCourseEnrollment[]>([]);
  const [message, setMessage] = useState("");
  const [courseForm, setCourseForm] = useState({
    title: "",
    description: "",
    status: "published" as SelfCourse["status"],
  });
  const [newCourse, setNewCourse] = useState({ title: "", description: "" });
  const [moduleForm, setModuleForm] = useState({ title: "", subtitle: "" });
  const [lessonForm, setLessonForm] = useState({
    moduleId: "",
    title: "",
    type: "material" as SelfCourseLessonType,
    durationMinutes: 10,
    content: "",
    videoUrl: "",
  });
  const [lessonMaterialFiles, setLessonMaterialFiles] = useState<
    Record<string, File[]>
  >({});
  const [lessonMaterialUploading, setLessonMaterialUploading] = useState<
    Record<string, boolean>
  >({});
  const [lessonDrafts, setLessonDrafts] = useState<
    Record<
      string,
      Partial<{
        type: SelfCourseLessonType;
        durationMinutes: number;
        content: string;
        videoUrl: string;
        passingScore: number;
        questions: SelfCourseQuizQuestion[];
      }>
    >
  >({});
  const [openModules, setOpenModules] = useState<Record<string, boolean>>({});
  const [selectedLessonKey, setSelectedLessonKey] = useState("");
  const [isModuleModalOpen, setIsModuleModalOpen] = useState(false);
  const [isLessonModalOpen, setIsLessonModalOpen] = useState(false);
  const [reviewState, setReviewState] = useState<
    Record<string, { score: string; status: string; adminComment: string }>
  >({});

  const loadCourses = async () => {
    const data = await selfCoursesApi.listAdmin();
    setSelectedId((current) => current || data[0]?._id || "");
  };

  const loadSelected = async (id: string) => {
    if (!id) return;
    const data = await selfCoursesApi.listEnrollments(id);
    setSelectedCourse(data.course);
    setEnrollments(data.enrollments);
    setOpenModules((prev) => {
      const next: Record<string, boolean> = {};
      data.course.modules.forEach((module, index) => {
        next[module._id] = prev[module._id] ?? index === 0;
      });
      return next;
    });
    setSelectedLessonKey((current) => {
      const lessonKeys = data.course.modules.flatMap((module) =>
        module.lessons.map((lesson) => `${module._id}-${lesson._id}`)
      );
      return current && lessonKeys.includes(current)
        ? current
        : lessonKeys[0] || "";
    });
    setCourseForm({
      title: data.course.title,
      description: data.course.description || "",
      status: data.course.status,
    });
  };

  useEffect(() => {
    loadCourses().catch(() => setMessage("Не вдалося завантажити курси."));
  }, []);

  useEffect(() => {
    loadSelected(selectedId).catch(() =>
      setMessage("Не вдалося завантажити деталі курсу.")
    );
  }, [selectedId]);

  const lessonsById = useMemo(() => {
    const map = new Map<string, string>();
    selectedCourse?.modules.forEach((module) => {
      module.lessons.forEach((lesson) => map.set(lesson._id, lesson.title));
    });
    return map;
  }, [selectedCourse]);

  const selectedLessonData = useMemo(() => {
    if (!selectedCourse || !selectedLessonKey) return null;
    for (const courseModule of selectedCourse.modules) {
      for (const lesson of courseModule.lessons) {
        const key = `${courseModule._id}-${lesson._id}`;
        if (key === selectedLessonKey) {
          return { module: courseModule, lesson, key };
        }
      }
    }
    return null;
  }, [selectedCourse, selectedLessonKey]);

  const selectedLessonDraft = useMemo(() => {
    if (!selectedLessonData) return null;
    const draft = lessonDrafts[selectedLessonData.key] || {};
    const type =
      draft.type ||
      (selectedLessonData.lesson.type === "dialog"
        ? "material"
        : selectedLessonData.lesson.type);

    return {
      type,
      durationMinutes:
        draft.durationMinutes ?? selectedLessonData.lesson.durationMinutes,
      content: draft.content ?? selectedLessonData.lesson.content ?? "",
      videoUrl: draft.videoUrl ?? selectedLessonData.lesson.videoUrl ?? "",
      passingScore:
        draft.passingScore ?? selectedLessonData.lesson.passingScore ?? 70,
      questions:
        draft.questions ??
        (selectedLessonData.lesson.questions || []).map((question) => ({
          ...question,
          options: question.options.map((option) => ({ ...option })),
        })),
    };
  }, [lessonDrafts, selectedLessonData]);

  const getLessonTypeLabel = (type: SelfCourseLessonType) => {
    if (type === "dialog") return "Матеріал";
    return lessonTypeOptions.find((option) => option.value === type)?.label || type;
  };

  const getModuleDuration = (courseModule: SelfCourse["modules"][number]) =>
    courseModule.lessons.reduce(
      (total, lesson) => total + (lesson.durationMinutes || 0),
      0
    );

  const getModuleMaterialsCount = (
    courseModule: SelfCourse["modules"][number]
  ) =>
    courseModule.lessons.reduce(
      (total, lesson) => total + (lesson.materials?.length || 0),
      0
    );

  const toggleModule = (moduleId: string) => {
    setOpenModules((prev) => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  const updateSelectedLessonDraft = (
    patch: Partial<{
      type: SelfCourseLessonType;
      durationMinutes: number;
      content: string;
      videoUrl: string;
      passingScore: number;
      questions: SelfCourseQuizQuestion[];
    }>
  ) => {
    if (!selectedLessonData) return;
    setLessonDrafts((prev) => ({
      ...prev,
      [selectedLessonData.key]: {
        ...prev[selectedLessonData.key],
        ...patch,
      },
    }));
  };

  const saveCourse = async () => {
    if (!selectedCourse) return;
    const course = await selfCoursesApi.updateAdmin(selectedCourse._id, courseForm);
    setSelectedCourse(course);
    setMessage("Курс оновлено.");
  };

  const createCourse = async () => {
    if (!newCourse.title.trim()) {
      setMessage("Вкажіть назву курсу.");
      return;
    }
    const course = await selfCoursesApi.createAdmin(newCourse);
    setSelectedId(course._id);
    setNewCourse({ title: "", description: "" });
    setMessage("Новий курс створено як чернетку.");
  };

  const addModule = async () => {
    if (!selectedCourse) return;
    const course = await selfCoursesApi.addModule(selectedCourse._id, moduleForm);
    setSelectedCourse(course);
    const addedModule = course.modules[course.modules.length - 1];
    if (addedModule) {
      setOpenModules((prev) => ({ ...prev, [addedModule._id]: true }));
    }
    setModuleForm({ title: "", subtitle: "" });
    setIsModuleModalOpen(false);
    setMessage("Модуль додано.");
  };

  const addLesson = async () => {
    if (!selectedCourse || !lessonForm.moduleId) {
      setMessage("Оберіть модуль для нового уроку.");
      return;
    }
    const course = await selfCoursesApi.addLesson(
      selectedCourse._id,
      lessonForm.moduleId,
      lessonForm
    );
    setSelectedCourse(course);
    const targetModuleId = lessonForm.moduleId;
    const targetModule = course.modules.find(
      (courseModule) => courseModule._id === targetModuleId
    );
    const addedLesson = targetModule?.lessons[targetModule.lessons.length - 1];
    if (addedLesson) {
      setOpenModules((prev) => ({ ...prev, [targetModuleId]: true }));
      setSelectedLessonKey(`${targetModuleId}-${addedLesson._id}`);
    }
    setLessonForm({
      moduleId: "",
      title: "",
      type: "material",
      durationMinutes: 10,
      content: "",
      videoUrl: "",
    });
    setIsLessonModalOpen(false);
    setMessage("Урок додано.");
  };

  const addLessonMaterials = async (
    moduleId: string,
    lessonId: string
  ) => {
    if (!selectedCourse) return;
    const key = `${moduleId}-${lessonId}`;
    const files = lessonMaterialFiles[key] || [];
    if (files.length === 0) {
      setMessage("Оберіть файл матеріалу для уроку.");
      return;
    }

    setLessonMaterialUploading((prev) => ({ ...prev, [key]: true }));
    setMessage("Завантажуємо файл...");

    try {
      await selfCoursesApi.addLessonMaterials(
        selectedCourse._id,
        moduleId,
        lessonId,
        files
      );
      await loadSelected(selectedCourse._id);
      setLessonMaterialFiles((prev) => ({ ...prev, [key]: [] }));
      setMessage("Матеріал додано до уроку.");
    } catch (error) {
      setMessage("Не вдалося завантажити файл. Перевір бекенд або Dropbox.");
    } finally {
      setLessonMaterialUploading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const saveLessonDetails = async () => {
    if (!selectedCourse || !selectedLessonData || !selectedLessonDraft) return;
    const course = await selfCoursesApi.updateLesson(
      selectedCourse._id,
      selectedLessonData.module._id,
      selectedLessonData.lesson._id,
      {
        type: selectedLessonDraft.type,
        durationMinutes: selectedLessonDraft.durationMinutes,
        content: selectedLessonDraft.content,
        videoUrl:
          selectedLessonDraft.type === "video"
            ? selectedLessonDraft.videoUrl
            : "",
        passingScore: selectedLessonDraft.passingScore,
        questions:
          selectedLessonDraft.type === "quiz"
            ? selectedLessonDraft.questions
            : [],
      }
    );
    setSelectedCourse(course);
    setLessonDrafts((prev) => {
      const next = { ...prev };
      delete next[selectedLessonData.key];
      return next;
    });
    setMessage("Урок оновлено.");
  };

  const updateQuizQuestion = (
    questionIndex: number,
    patch: Partial<SelfCourseQuizQuestion>
  ) => {
    if (!selectedLessonDraft) return;
    const questions = [...selectedLessonDraft.questions];
    questions[questionIndex] = {
      ...questions[questionIndex],
      ...patch,
    };

    if (patch.allowMultiple === false) {
      let firstCorrectFound = false;
      questions[questionIndex].options = questions[questionIndex].options.map(
        (option) => {
          if (!option.isCorrect) return option;
          if (!firstCorrectFound) {
            firstCorrectFound = true;
            return option;
          }
          return { ...option, isCorrect: false };
        }
      );
    }

    updateSelectedLessonDraft({ questions });
  };

  const updateQuizOption = (
    questionIndex: number,
    optionIndex: number,
    patch: Partial<SelfCourseQuizQuestion["options"][number]>
  ) => {
    if (!selectedLessonDraft) return;
    const questions = [...selectedLessonDraft.questions];
    const question = questions[questionIndex];
    const options = [...question.options];
    options[optionIndex] = { ...options[optionIndex], ...patch };
    questions[questionIndex] = { ...question, options };
    updateSelectedLessonDraft({ questions });
  };

  const setQuizOptionCorrect = (
    questionIndex: number,
    optionIndex: number,
    checked: boolean
  ) => {
    if (!selectedLessonDraft) return;
    const questions = [...selectedLessonDraft.questions];
    const question = questions[questionIndex];
    const options = question.options.map((option, index) => {
      if (question.allowMultiple) {
        return index === optionIndex ? { ...option, isCorrect: checked } : option;
      }

      return { ...option, isCorrect: index === optionIndex };
    });
    questions[questionIndex] = { ...question, options };
    updateSelectedLessonDraft({ questions });
  };

  const addQuizQuestion = () => {
    if (!selectedLessonDraft) return;
    updateSelectedLessonDraft({
      questions: [
        ...selectedLessonDraft.questions,
        {
          question: "Нове питання",
          allowMultiple: false,
          options: [
            { text: "Варіант 1", isCorrect: true },
            { text: "Варіант 2", isCorrect: false },
          ],
        },
      ],
    });
  };

  const removeQuizQuestion = (questionIndex: number) => {
    if (!selectedLessonDraft) return;
    updateSelectedLessonDraft({
      questions: selectedLessonDraft.questions.filter(
        (_question, index) => index !== questionIndex
      ),
    });
  };

  const addQuizOption = (questionIndex: number) => {
    if (!selectedLessonDraft) return;
    const questions = [...selectedLessonDraft.questions];
    const question = questions[questionIndex];
    questions[questionIndex] = {
      ...question,
      options: [
        ...question.options,
        { text: `Варіант ${question.options.length + 1}`, isCorrect: false },
      ],
    };
    updateSelectedLessonDraft({ questions });
  };

  const removeQuizOption = (questionIndex: number, optionIndex: number) => {
    if (!selectedLessonDraft) return;
    const questions = [...selectedLessonDraft.questions];
    const question = questions[questionIndex];
    questions[questionIndex] = {
      ...question,
      options: question.options.filter((_option, index) => index !== optionIndex),
    };
    updateSelectedLessonDraft({ questions });
  };

  const reviewSubmission = async (
    enrollmentId: string,
    submissionId: string
  ) => {
    if (!selectedCourse) return;
    const key = `${enrollmentId}-${submissionId}`;
    const state = reviewState[key] || {
      score: "",
      status: "reviewed",
      adminComment: "",
    };
    await selfCoursesApi.reviewSubmission(
      selectedCourse._id,
      enrollmentId,
      submissionId,
      {
        score: state.score === "" ? "" : Number(state.score),
        status: state.status,
        adminComment: state.adminComment,
      }
    );
    await loadSelected(selectedCourse._id);
    setMessage("Роботу перевірено.");
  };

  return (
    <section className="min-h-full bg-[#f4f7fb] px-4 pb-6 pt-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1500px]">
        <div className="mb-6 flex flex-col gap-4 border-b border-[#E0DCD5] pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.14em] text-[#8a6f00]">
              Кабінет адміністратора
            </p>
            <h1 className="mt-1 text-3xl font-black uppercase leading-none tracking-[-0.05em] text-black sm:text-5xl">
              Самостійні курси
            </h1>
          </div>
          <div className="grid gap-2 sm:grid-cols-[260px_1fr_auto]">
            <input
              value={newCourse.title}
              onChange={(event) =>
                setNewCourse((prev) => ({ ...prev, title: event.target.value }))
              }
              className="h-11 rounded-2xl border border-[#DCD7CF] px-4 text-sm font-bold"
              placeholder="Назва нового курсу"
            />
            <input
              value={newCourse.description}
              onChange={(event) =>
                setNewCourse((prev) => ({
                  ...prev,
                  description: event.target.value,
                }))
              }
              className="h-11 rounded-2xl border border-[#DCD7CF] px-4 text-sm font-bold"
              placeholder="Короткий опис"
            />
            <button
              type="button"
              onClick={createCourse}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-4 text-sm font-black text-black shadow-[3px_3px_0_#000]"
            >
              <Plus size={17} /> Курс
            </button>
          </div>
        </div>

        {message && (
          <div className="mb-5 rounded-xl border border-[#E5D2C5] bg-[#FFF8F3] px-4 py-3 text-sm font-semibold text-[#8A4A2E]">
            {message}
          </div>
        )}

        <div>
          {selectedCourse && (
            <main className="space-y-5">
              <div className="rounded-3xl border border-[#E3DED6] bg-white p-5">
                <div className="mb-4 flex items-center gap-3">
                  <Edit3 className="text-[#8a6f00]" size={20} />
                  <h2 className="text-xl font-black text-black">
                    Редагування курсу
                  </h2>
                </div>
                <div className="grid gap-3 lg:grid-cols-[1fr_170px_auto]">
                  <input
                    value={courseForm.title}
                    onChange={(event) =>
                      setCourseForm((prev) => ({
                        ...prev,
                        title: event.target.value,
                      }))
                    }
                    className="h-12 rounded-2xl border border-[#DCD7CF] px-4 text-sm font-bold"
                  />
                  <select
                    value={courseForm.status}
                    onChange={(event) =>
                      setCourseForm((prev) => ({
                        ...prev,
                        status: event.target.value as SelfCourse["status"],
                      }))
                    }
                    className="h-12 rounded-2xl border border-[#DCD7CF] px-4 text-sm font-bold"
                  >
                    <option value="draft">Чернетка</option>
                    <option value="published">Опубліковано</option>
                    <option value="archived">Архів</option>
                  </select>
                  <button
                    type="button"
                    onClick={saveCourse}
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-5 text-sm font-black text-black shadow-[3px_3px_0_#000]"
                  >
                    <Save size={17} /> Зберегти
                  </button>
                </div>
                <textarea
                  value={courseForm.description}
                  onChange={(event) =>
                    setCourseForm((prev) => ({
                      ...prev,
                      description: event.target.value,
                    }))
                  }
                  className="mt-3 min-h-[90px] w-full rounded-2xl border border-[#DCD7CF] p-4 text-sm font-bold"
                />
              </div>

              <div>
                <div className="rounded-3xl border border-[#E3DED6] bg-white p-5">
                  <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                      <BookOpen className="text-[#8a6f00]" size={20} />
                      <h2 className="text-xl font-black text-black">
                        Структура курсу
                      </h2>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setIsModuleModalOpen(true)}
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border-2 border-black bg-[#ffd400] px-4 text-xs font-black text-black shadow-[3px_3px_0_#000]"
                      >
                        <Plus size={16} /> Модуль
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsLessonModalOpen(true)}
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border-2 border-black bg-white px-4 text-xs font-black text-black shadow-[3px_3px_0_#ffd400]"
                      >
                        <Plus size={16} /> Урок
                      </button>
                    </div>
                  </div>
                  <div className="grid items-start gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
                    <aside className="rounded-2xl border border-[#E8E1D8] bg-[#fffdf7] p-3 xl:sticky xl:top-5 xl:max-h-[calc(100dvh-40px)] xl:overflow-y-auto xl:overscroll-contain">
                      <p className="mb-3 px-2 text-xs font-black uppercase tracking-[0.14em] text-[#8a6f00]">
                        Навігація курсу
                      </p>
                      <div className="space-y-2">
                        {selectedCourse.modules.map((module) => {
                          const isModuleOpen = Boolean(openModules[module._id]);
                          const moduleDuration = getModuleDuration(module);
                          const moduleMaterialsCount =
                            getModuleMaterialsCount(module);

                          return (
                            <section
                              key={module._id}
                              className="overflow-hidden rounded-2xl border border-[#E8E1D8] bg-white"
                            >
                              <button
                                type="button"
                                onClick={() => toggleModule(module._id)}
                                className="flex w-full items-start justify-between gap-3 px-3 py-3 text-left transition hover:bg-[#fff7d7]"
                              >
                                <div className="min-w-0">
                                  <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#8a6f00]">
                                    {module.subtitle || `Модуль ${module.order}`}
                                  </p>
                                  <h3 className="mt-1 text-sm font-black text-black">
                                    {module.title}
                                  </h3>
                                  <div className="mt-2 flex flex-wrap gap-1.5">
                                    <span className="rounded-full bg-[#F7F4EF] px-2.5 py-1 text-[11px] font-black text-[#625C55]">
                                      {module.lessons.length} уроків
                                    </span>
                                    <span className="rounded-full bg-[#F7F4EF] px-2.5 py-1 text-[11px] font-black text-[#625C55]">
                                      {moduleDuration} хв
                                    </span>
                                    <span className="rounded-full bg-[#F7F4EF] px-2.5 py-1 text-[11px] font-black text-[#625C55]">
                                      {moduleMaterialsCount} файлів
                                    </span>
                                  </div>
                                </div>
                                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border-2 border-black bg-[#ffd400] text-black shadow-[2px_2px_0_#000]">
                                  {isModuleOpen ? (
                                    <ChevronUp size={16} />
                                  ) : (
                                    <ChevronDown size={16} />
                                  )}
                                </span>
                              </button>

                              {isModuleOpen && (
                                <div className="space-y-2 border-t border-[#E8E1D8] bg-[#fffdf7] p-2">
                                  {module.lessons.length === 0 ? (
                                    <div className="rounded-xl border border-dashed border-[#DCD7CF] bg-white p-3 text-xs font-bold text-[#625C55]">
                                      У цьому модулі ще немає уроків.
                                    </div>
                                  ) : (
                                    module.lessons.map((lesson) => {
                                      const key = `${module._id}-${lesson._id}`;
                                      const isSelected =
                                        selectedLessonKey === key;

                                      return (
                                        <button
                                          key={lesson._id}
                                          type="button"
                                          onClick={() => setSelectedLessonKey(key)}
                                          className={`w-full rounded-xl border px-3 py-3 text-left transition ${
                                            isSelected
                                              ? "border-black bg-[#ffd400] shadow-[2px_2px_0_#000]"
                                              : "border-[#E8E1D8] bg-white hover:border-black"
                                          }`}
                                        >
                                          <div className="flex flex-wrap items-center gap-1.5">
                                            <span
                                              className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase ${
                                                isSelected
                                                  ? "bg-black text-white"
                                                  : "bg-[#ffd400] text-black"
                                              }`}
                                            >
                                              {getLessonTypeLabel(lesson.type)}
                                            </span>
                                            <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[10px] font-black text-[#625C55]">
                                              <Clock size={12} />
                                              {lesson.durationMinutes} хв
                                            </span>
                                          </div>
                                          <h4 className="mt-2 text-sm font-black text-black">
                                            {lesson.title}
                                          </h4>
                                        </button>
                                      );
                                    })
                                  )}
                                </div>
                              )}
                            </section>
                          );
                        })}
                      </div>
                    </aside>

                    <div className="min-h-[430px] rounded-2xl border border-[#E8E1D8] bg-white p-5">
                      {selectedLessonData ? (
                        <div className="space-y-4">
                          <div className="flex flex-col gap-3 border-b border-[#E8E1D8] pb-4 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                              <p className="text-xs font-black uppercase tracking-[0.14em] text-[#8a6f00]">
                                {selectedLessonData.module.subtitle ||
                                  `Модуль ${selectedLessonData.module.order}`}
                              </p>
                              <h3 className="mt-1 text-2xl font-black text-black">
                                {selectedLessonData.lesson.title}
                              </h3>
                              <p className="mt-1 text-sm font-bold text-[#625C55]">
                                {selectedLessonData.module.title}
                              </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <span className="rounded-full bg-[#ffd400] px-3 py-1 text-xs font-black uppercase text-black">
                                {getLessonTypeLabel(
                                  selectedLessonDraft?.type ||
                                    selectedLessonData.lesson.type
                                )}
                              </span>
                              <span className="inline-flex items-center gap-1 rounded-full bg-[#F7F4EF] px-3 py-1 text-xs font-black text-[#625C55]">
                                <Clock size={14} />
                                {selectedLessonDraft?.durationMinutes ??
                                  selectedLessonData.lesson.durationMinutes}{" "}
                                хв
                              </span>
                              {(["material", "lab"] as string[]).includes(
                                selectedLessonDraft?.type ||
                                  selectedLessonData.lesson.type
                              ) && (
                                <span className="rounded-full bg-[#F7F4EF] px-3 py-1 text-xs font-black text-[#625C55]">
                                  {selectedLessonData.lesson.materials?.length || 0}{" "}
                                  файлів
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="rounded-2xl border border-[#E8E1D8] bg-[#fffdf7] p-4 text-sm font-bold leading-relaxed text-[#3F3A34]">
                            <div className="grid gap-4">
                              <div className="grid gap-3 md:grid-cols-[minmax(180px,260px)_140px]">
                                <label className="grid gap-2 text-xs font-black uppercase tracking-[0.1em] text-[#8a6f00]">
                                  Тип
                                  <select
                                    value={
                                      selectedLessonDraft?.type ||
                                      selectedLessonData.lesson.type
                                    }
                                    onChange={(event) =>
                                      updateSelectedLessonDraft({
                                        type: event.target
                                          .value as SelfCourseLessonType,
                                      })
                                    }
                                    className="h-11 rounded-2xl border border-[#DCD7CF] bg-white px-4 text-sm font-black normal-case tracking-normal text-black"
                                  >
                                    {lessonTypeOptions.map((option) => (
                                      <option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <label className="grid gap-2 text-xs font-black uppercase tracking-[0.1em] text-[#8a6f00]">
                                  Хвилини
                                  <input
                                    type="number"
                                    min={1}
                                    value={
                                      selectedLessonDraft?.durationMinutes ??
                                      selectedLessonData.lesson.durationMinutes
                                    }
                                    onChange={(event) =>
                                      updateSelectedLessonDraft({
                                        durationMinutes: Number(
                                          event.target.value
                                        ),
                                      })
                                    }
                                    className="h-11 rounded-2xl border border-[#DCD7CF] bg-white px-4 text-sm font-black normal-case tracking-normal text-black"
                                  />
                                </label>
                              </div>
                              <label className="grid gap-2 text-xs font-black uppercase tracking-[0.1em] text-[#8a6f00]">
                                Коментар / текст
                                <textarea
                                  value={
                                    selectedLessonDraft?.content ??
                                    selectedLessonData.lesson.content ??
                                    ""
                                  }
                                  onChange={(event) =>
                                    updateSelectedLessonDraft({
                                      content: event.target.value,
                                    })
                                  }
                                  className="min-h-[120px] resize-y rounded-2xl border border-[#DCD7CF] bg-white p-4 text-sm font-bold normal-case tracking-normal text-black"
                                  placeholder="Короткий текст, коментар або інструкція до уроку"
                                />
                              </label>
                              <button
                                type="button"
                                onClick={saveLessonDetails}
                                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-5 text-sm font-black text-black shadow-[3px_3px_0_#000] sm:ml-auto sm:w-auto"
                              >
                                <Save size={16} /> Зберегти
                              </button>
                            </div>
                          </div>

                          {(selectedLessonDraft?.type ||
                            selectedLessonData.lesson.type) === "video" && (
                            <div className="rounded-2xl border border-[#E8E1D8] bg-[#fffdf7] p-4">
                              <p className="mb-2 text-xs font-black uppercase tracking-[0.12em] text-[#8a6f00]">
                                Посилання на відео
                              </p>
                              <input
                                value={
                                  selectedLessonDraft?.videoUrl ??
                                  selectedLessonData.lesson.videoUrl ??
                                  ""
                                }
                                onChange={(event) =>
                                  updateSelectedLessonDraft({
                                    videoUrl: event.target.value,
                                  })
                                }
                                className="h-11 w-full rounded-2xl border border-[#DCD7CF] bg-white px-4 text-sm font-bold text-black"
                                placeholder="YouTube, Vimeo, Google Drive..."
                              />
                              {(selectedLessonDraft?.videoUrl ||
                                selectedLessonData.lesson.videoUrl) && (
                                <a
                                  href={
                                    selectedLessonDraft?.videoUrl ||
                                    selectedLessonData.lesson.videoUrl
                                  }
                                  target="_blank"
                                  rel="noreferrer"
                                  className="mt-3 inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.1em] text-[#8a6f00]"
                                >
                                  Відкрити відео <ExternalLink size={14} />
                                </a>
                              )}
                            </div>
                          )}

                          {(selectedLessonDraft?.type ||
                            selectedLessonData.lesson.type) === "quiz" &&
                            selectedLessonDraft && (
                              <div className="rounded-2xl border border-[#E8E1D8] bg-[#fffdf7] p-4">
                                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                  <div>
                                    <p className="text-xs font-black uppercase tracking-[0.12em] text-[#8a6f00]">
                                      Питання тесту
                                    </p>
                                    <p className="mt-1 text-sm font-bold text-[#625C55]">
                                      Тут можна змінити питання, варіанти та
                                      позначити правильні відповіді.
                                    </p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={addQuizQuestion}
                                    className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border-2 border-black bg-[#ffd400] px-4 text-xs font-black text-black shadow-[3px_3px_0_#000]"
                                  >
                                    <Plus size={16} /> Питання
                                  </button>
                                </div>

                                <label className="mb-4 grid max-w-[220px] gap-2 text-xs font-black uppercase tracking-[0.1em] text-[#8a6f00]">
                                  Прохідний бал, %
                                  <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={selectedLessonDraft.passingScore}
                                    onChange={(event) =>
                                      updateSelectedLessonDraft({
                                        passingScore: Number(event.target.value),
                                      })
                                    }
                                    className="h-10 rounded-xl border border-[#DCD7CF] bg-white px-3 text-sm font-black normal-case tracking-normal text-black"
                                  />
                                </label>

                                <div className="space-y-4">
                                  {selectedLessonDraft.questions.length === 0 ? (
                                    <div className="rounded-2xl border border-dashed border-[#DCD7CF] bg-white p-4 text-sm font-bold text-[#817A72]">
                                      Питання ще не додані.
                                    </div>
                                  ) : (
                                    selectedLessonDraft.questions.map(
                                      (question, questionIndex) => (
                                        <div
                                          key={question._id || questionIndex}
                                          className="rounded-2xl border border-[#E8E1D8] bg-white p-4"
                                        >
                                          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                                            <span className="rounded-full bg-[#ffd400] px-3 py-1 text-xs font-black text-black">
                                              Питання {questionIndex + 1}
                                            </span>
                                            <button
                                              type="button"
                                              onClick={() =>
                                                removeQuizQuestion(questionIndex)
                                              }
                                              className="inline-flex h-8 items-center justify-center rounded-lg border border-[#F2C8C8] bg-[#FFF3F3] px-3 text-xs font-black text-[#B42318]"
                                            >
                                              Видалити
                                            </button>
                                          </div>

                                          <textarea
                                            value={question.question}
                                            onChange={(event) =>
                                              updateQuizQuestion(questionIndex, {
                                                question: event.target.value,
                                              })
                                            }
                                            className="min-h-[80px] w-full rounded-xl border border-[#DCD7CF] p-3 text-sm font-bold text-black"
                                            placeholder="Текст питання"
                                          />

                                          <label className="mt-3 flex items-center gap-2 rounded-xl border border-[#E8E1D8] bg-[#fffdf7] px-3 py-2 text-sm font-black text-black">
                                            <input
                                              type="checkbox"
                                              checked={Boolean(question.allowMultiple)}
                                              onChange={(event) =>
                                                updateQuizQuestion(questionIndex, {
                                                  allowMultiple:
                                                    event.target.checked,
                                                })
                                              }
                                            />
                                            Можна обрати кілька правильних
                                            відповідей
                                          </label>

                                          <div className="mt-3 space-y-2">
                                            {question.options.map(
                                              (option, optionIndex) => (
                                                <div
                                                  key={option._id || optionIndex}
                                                  className={`grid gap-2 rounded-xl border p-3 md:grid-cols-[auto_minmax(0,1fr)_auto] md:items-center ${
                                                    option.isCorrect
                                                      ? "border-[#118221] bg-[#F0FFF4]"
                                                      : "border-[#E8E1D8] bg-[#F7F4EF]"
                                                  }`}
                                                >
                                                  <label className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.08em] text-[#625C55]">
                                                    <input
                                                      type={
                                                        question.allowMultiple
                                                          ? "checkbox"
                                                          : "radio"
                                                      }
                                                      name={`correct-${selectedLessonData.key}-${questionIndex}`}
                                                      checked={Boolean(
                                                        option.isCorrect
                                                      )}
                                                      onChange={(event) =>
                                                        setQuizOptionCorrect(
                                                          questionIndex,
                                                          optionIndex,
                                                          event.target.checked
                                                        )
                                                      }
                                                    />
                                                    {option.isCorrect
                                                      ? "Правильна"
                                                      : "Правильна?"}
                                                  </label>
                                                  <input
                                                    value={option.text}
                                                    onChange={(event) =>
                                                      updateQuizOption(
                                                        questionIndex,
                                                        optionIndex,
                                                        {
                                                          text:
                                                            event.target.value,
                                                        }
                                                      )
                                                    }
                                                    className="h-10 rounded-xl border border-[#DCD7CF] bg-white px-3 text-sm font-bold text-black"
                                                    placeholder="Варіант відповіді"
                                                  />
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      removeQuizOption(
                                                        questionIndex,
                                                        optionIndex
                                                      )
                                                    }
                                                    className="h-9 rounded-lg border border-[#F2C8C8] bg-white px-3 text-xs font-black text-[#B42318]"
                                                  >
                                                    Прибрати
                                                  </button>
                                                </div>
                                              )
                                            )}
                                          </div>

                                          <button
                                            type="button"
                                            onClick={() =>
                                              addQuizOption(questionIndex)
                                            }
                                            className="mt-3 inline-flex h-9 items-center justify-center gap-2 rounded-xl border border-black bg-white px-3 text-xs font-black text-black"
                                          >
                                            <Plus size={14} /> Варіант відповіді
                                          </button>
                                        </div>
                                      )
                                    )
                                  )}
                                </div>
                              </div>
                            )}

                          {(["material", "lab"] as string[]).includes(
                            selectedLessonDraft?.type ||
                              selectedLessonData.lesson.type
                          ) && (
                            <>
                              <ProgramMaterials
                                materials={selectedLessonData.lesson.materials || []}
                                title="Файли уроку"
                                emptyText="Файли ще не додані."
                              />

                              <div className="rounded-2xl border border-[#E8E1D8] bg-[#fffdf7] p-4">
                                <p className="mb-2 text-xs font-black uppercase tracking-[0.12em] text-[#8a6f00]">
                                  Додати файл
                                </p>
                                {lessonMaterialUploading[
                                  selectedLessonData.key
                                ] && (
                                  <div className="mb-3 flex items-center gap-3 rounded-2xl border border-[#E8E1D8] bg-white px-4 py-3 text-sm font-black text-black">
                                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-black border-t-transparent" />
                                    Файл завантажується, зачекай...
                                  </div>
                                )}
                                <MaterialDropzone
                                  files={
                                    lessonMaterialFiles[selectedLessonData.key] ||
                                    []
                                  }
                                  onFilesChange={(files) =>
                                    setLessonMaterialFiles((prev) => ({
                                      ...prev,
                                      [selectedLessonData.key]: files,
                                    }))
                                  }
                                  maxFiles={3}
                                  accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.webp"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    addLessonMaterials(
                                      selectedLessonData.module._id,
                                      selectedLessonData.lesson._id
                                    )
                                  }
                                  disabled={
                                    lessonMaterialUploading[
                                      selectedLessonData.key
                                    ]
                                  }
                                  className="mt-3 inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border-2 border-black bg-[#ffd400] px-4 text-xs font-black text-black shadow-[3px_3px_0_#000] disabled:cursor-wait disabled:opacity-60 sm:w-auto"
                                >
                                  {lessonMaterialUploading[
                                    selectedLessonData.key
                                  ] && (
                                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-black border-t-transparent" />
                                  )}
                                  {lessonMaterialUploading[
                                    selectedLessonData.key
                                  ]
                                    ? "Завантаження..."
                                    : "Завантажити файл"}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="grid min-h-[360px] place-items-center rounded-2xl border border-dashed border-[#DCD7CF] bg-[#fffdf7] p-6 text-center">
                          <div>
                            <BookOpen
                              className="mx-auto text-[#8a6f00]"
                              size={36}
                            />
                            <h3 className="mt-3 text-xl font-black text-black">
                              Обери урок зліва
                            </h3>
                            <p className="mt-2 max-w-md text-sm font-bold text-[#625C55]">
                              Тут будуть матеріали, опис, відео та завантаження
                              файлів для конкретного уроку.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-[#E3DED6] bg-white p-5">
                <div className="mb-4 flex items-center gap-3">
                  <Users className="text-[#8a6f00]" size={20} />
                  <h2 className="text-xl font-black text-black">
                    Учасники та роботи
                  </h2>
                </div>
                <div className="space-y-4">
                  {enrollments.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-[#DCD7CF] p-5 text-sm font-bold text-[#817A72]">
                      Учасники ще не починали цей курс.
                    </div>
                  ) : (
                    enrollments.map((enrollment) => {
                      const user =
                        typeof enrollment.userId === "string"
                          ? null
                          : enrollment.userId;
                      return (
                        <div
                          key={enrollment._id}
                          className="rounded-2xl border border-[#E8E1D8] bg-[#fffdf7] p-4"
                        >
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <h3 className="font-black text-black">
                                {user?.username || "Учасник"}
                              </h3>
                              <p className="text-xs font-bold text-[#817A72]">
                                {user?.telephone} {user?.email ? `· ${user.email}` : ""}
                              </p>
                            </div>
                            <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-sm font-black text-[#118221]">
                              <CheckCircle2 size={15} /> {enrollment.progress}%
                            </span>
                          </div>

                          <div className="mt-4 space-y-3">
                            {enrollment.submissions.length === 0 ? (
                              <p className="text-sm font-bold text-[#817A72]">
                                Робіт ще немає.
                              </p>
                            ) : (
                              enrollment.submissions.map((submission) => {
                                const key = `${enrollment._id}-${submission._id}`;
                                const state = reviewState[key] || {
                                  score:
                                    submission.score === null ||
                                    submission.score === undefined
                                      ? ""
                                      : String(submission.score),
                                  status: submission.status,
                                  adminComment: submission.adminComment || "",
                                };

                                return (
                                  <div
                                    key={submission._id}
                                    className="rounded-2xl bg-white p-4"
                                  >
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                      <div>
                                        <p className="text-sm font-black text-black">
                                          {lessonsById.get(String(submission.lessonId)) ||
                                            "Завдання"}
                                        </p>
                                        <a
                                          href={submission.link}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-[#155EEF]"
                                        >
                                          Відкрити роботу <ExternalLink size={12} />
                                        </a>
                                      </div>
                                      <span className="rounded-full bg-[#F7F4EF] px-3 py-1 text-xs font-black text-[#625C55]">
                                        {submission.status}
                                      </span>
                                    </div>
                                    {submission.comment && (
                                      <p className="mt-3 text-sm font-semibold text-[#625C55]">
                                        {submission.comment}
                                      </p>
                                    )}
                                    <div className="mt-3 grid gap-3 md:grid-cols-[120px_170px_minmax(0,1fr)_auto]">
                                      <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={state.score}
                                        onChange={(event) =>
                                          setReviewState((prev) => ({
                                            ...prev,
                                            [key]: {
                                              ...state,
                                              score: event.target.value,
                                            },
                                          }))
                                        }
                                        className="h-10 rounded-xl border border-[#DCD7CF] px-3 text-sm font-bold"
                                        placeholder="Бал"
                                      />
                                      <select
                                        value={state.status}
                                        onChange={(event) =>
                                          setReviewState((prev) => ({
                                            ...prev,
                                            [key]: {
                                              ...state,
                                              status: event.target.value,
                                            },
                                          }))
                                        }
                                        className="h-10 rounded-xl border border-[#DCD7CF] px-3 text-sm font-bold"
                                      >
                                        <option value="reviewed">Перевірено</option>
                                        <option value="accepted">Прийнято</option>
                                        <option value="needs_revision">На доопрацювання</option>
                                      </select>
                                      <input
                                        value={state.adminComment}
                                        onChange={(event) =>
                                          setReviewState((prev) => ({
                                            ...prev,
                                            [key]: {
                                              ...state,
                                              adminComment: event.target.value,
                                            },
                                          }))
                                        }
                                        className="h-10 rounded-xl border border-[#DCD7CF] px-3 text-sm font-bold"
                                        placeholder="Коментар"
                                      />
                                      <button
                                        type="button"
                                        onClick={() =>
                                          reviewSubmission(
                                            enrollment._id,
                                            submission._id
                                          )
                                        }
                                        className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border-2 border-black bg-[#ffd400] px-4 text-sm font-black text-black"
                                      >
                                        <FileCheck2 size={16} /> Оцінити
                                      </button>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </main>
          )}

          {selectedCourse && isModuleModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 px-4 py-6">
              <div className="w-full max-w-xl overflow-hidden rounded-3xl border-2 border-black bg-white shadow-[8px_8px_0_#ffd400]">
                <div className="flex items-center justify-between border-b-2 border-black bg-[#fffdf7] px-5 py-4">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.14em] text-[#8a6f00]">
                      Новий блок курсу
                    </p>
                    <h2 className="text-xl font-black text-black">
                      Додати модуль
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsModuleModalOpen(false)}
                    className="grid h-10 w-10 place-items-center rounded-xl border-2 border-black bg-white text-black"
                    aria-label="Закрити"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="grid gap-3 p-5">
                  <label className="grid gap-2 text-sm font-black text-[#3F3A34]">
                    Номер або підпис модуля
                    <input
                      value={moduleForm.subtitle}
                      onChange={(event) =>
                        setModuleForm((prev) => ({
                          ...prev,
                          subtitle: event.target.value,
                        }))
                      }
                      className="h-12 rounded-2xl border border-[#DCD7CF] px-4 text-sm font-bold"
                      placeholder="Наприклад: Модуль 4"
                    />
                  </label>
                  <label className="grid gap-2 text-sm font-black text-[#3F3A34]">
                    Назва модуля
                    <input
                      value={moduleForm.title}
                      onChange={(event) =>
                        setModuleForm((prev) => ({
                          ...prev,
                          title: event.target.value,
                        }))
                      }
                      className="h-12 rounded-2xl border border-[#DCD7CF] px-4 text-sm font-bold"
                      placeholder="Наприклад: Візуальні принципи дизайну"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={addModule}
                    className="mt-2 inline-flex h-12 items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-5 text-sm font-black text-black shadow-[3px_3px_0_#000]"
                  >
                    <Plus size={17} /> Додати модуль
                  </button>
                </div>
              </div>
            </div>
          )}

          {selectedCourse && isLessonModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 px-4 py-6">
              <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl border-2 border-black bg-white shadow-[8px_8px_0_#ffd400]">
                <div className="flex shrink-0 items-center justify-between border-b-2 border-black bg-[#fffdf7] px-5 py-4">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.14em] text-[#8a6f00]">
                      Контент модуля
                    </p>
                    <h2 className="text-xl font-black text-black">
                      Додати урок / завдання
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsLessonModalOpen(false)}
                    className="grid h-10 w-10 place-items-center rounded-xl border-2 border-black bg-white text-black"
                    aria-label="Закрити"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="grid gap-3 overflow-y-auto p-5">
                  <label className="grid gap-2 text-sm font-black text-[#3F3A34]">
                    Модуль
                    <select
                      value={lessonForm.moduleId}
                      onChange={(event) =>
                        setLessonForm((prev) => ({
                          ...prev,
                          moduleId: event.target.value,
                        }))
                      }
                      className="h-12 rounded-2xl border border-[#DCD7CF] px-4 text-sm font-bold"
                    >
                      <option value="">Оберіть модуль</option>
                      {selectedCourse.modules.map((module) => (
                        <option key={module._id} value={module._id}>
                          {module.subtitle} · {module.title}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm font-black text-[#3F3A34]">
                    Назва уроку
                    <input
                      value={lessonForm.title}
                      onChange={(event) =>
                        setLessonForm((prev) => ({
                          ...prev,
                          title: event.target.value,
                        }))
                      }
                      className="h-12 rounded-2xl border border-[#DCD7CF] px-4 text-sm font-bold"
                      placeholder="Наприклад: Основні елементи дизайну в Canva"
                    />
                  </label>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="grid gap-2 text-sm font-black text-[#3F3A34]">
                      Тип уроку
                      <select
                        value={lessonForm.type}
                        onChange={(event) =>
                          setLessonForm((prev) => ({
                            ...prev,
                            type: event.target.value as SelfCourseLessonType,
                          }))
                        }
                        className="h-12 rounded-2xl border border-[#DCD7CF] px-4 text-sm font-bold"
                      >
                        {lessonTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="grid gap-2 text-sm font-black text-[#3F3A34]">
                      Тривалість, хв
                      <input
                        type="number"
                        min={1}
                        value={lessonForm.durationMinutes}
                        onChange={(event) =>
                          setLessonForm((prev) => ({
                            ...prev,
                            durationMinutes: Number(event.target.value),
                          }))
                        }
                        className="h-12 rounded-2xl border border-[#DCD7CF] px-4 text-sm font-bold"
                        placeholder="10"
                      />
                    </label>
                  </div>
                  <label className="grid gap-2 text-sm font-black text-[#3F3A34]">
                    Посилання на відео
                    <input
                      value={lessonForm.videoUrl}
                      onChange={(event) =>
                        setLessonForm((prev) => ({
                          ...prev,
                          videoUrl: event.target.value,
                        }))
                      }
                      className="h-12 rounded-2xl border border-[#DCD7CF] px-4 text-sm font-bold"
                      placeholder="https://..."
                    />
                  </label>
                  <label className="grid gap-2 text-sm font-black text-[#3F3A34]">
                    Опис / інструкція
                    <textarea
                      value={lessonForm.content}
                      onChange={(event) =>
                        setLessonForm((prev) => ({
                          ...prev,
                          content: event.target.value,
                        }))
                      }
                      className="min-h-[120px] rounded-2xl border border-[#DCD7CF] p-4 text-sm font-bold"
                      placeholder="Що саме має побачити або зробити учасник"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={addLesson}
                    className="mt-2 inline-flex h-12 items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-5 text-sm font-black text-black shadow-[3px_3px_0_#000]"
                  >
                    <Plus size={17} /> Додати урок
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AdminSelfCoursesPage;
